import React, { useCallback, useMemo, useRef } from 'react'
import { View } from 'react-native'

import { Account } from '@ambire-common/interfaces/account'
import { SelectedAccountPortfolio } from '@ambire-common/interfaces/selectedAccount'
import GasTankIcon from '@common/assets/svg/GasTankIcon'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import { Trans, useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import { HIDDEN_BALANCE_MASK } from '@common/modules/dashboard/helpers/hiddenBalance'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import common from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import { getGasTankTokenDetails } from '@common/utils/getGasTankTokenDetails'
import useHasGasTank from '@web/hooks/useHasGasTank'
import { AnimatedPressable, useCustomHover } from '@web/hooks/useHover'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'

import { NEUTRAL_BACKGROUND_HOVERED } from '../../screens/styles'

type Props = {
  onPress: () => void
  portfolio: SelectedAccountPortfolio
  account: Account | null
  /** Hide-balance toggle: the tank's USD balance is a holding too, mask it. */
  balanceHidden?: boolean
}

// font families are different, with different "normal" line-heights, normalize them to vertically center better
const GAS_TANK_BUTTON_LINE_HEIGHT = 14

const GasTankButton = ({ onPress, portfolio, account, balanceHidden = false }: Props) => {
  const { t } = useTranslation()
  const buttonRef = useRef(null)
  const { hasGasTank, isViewOnly } = useHasGasTank({ account })
  const { theme, themeType } = useTheme()
  const textColor =
    themeType === THEME_TYPES.DARK ? theme.primaryBackgroundInverted : theme.primaryBackground
  const { networks } = useNetworksControllerState()

  const [bindGasTankBtnAim, removeTankBtnStyle] = useCustomHover({
    property: 'backgroundColor',
    values: { from: NEUTRAL_BACKGROUND_HOVERED, to: '#14183380' } // TODO: Remove hardcoded hex
  })

  const totalBalanceGasTankDetails = useMemo(
    () => getGasTankTokenDetails(portfolio, account, networks),
    [account, networks, portfolio]
  )

  const buttonState = useMemo(() => {
    // Only a tank we failed to LOAD is an error. An empty tank is the normal
    // state of a freshly created account and must still offer the top-up
    // (Trello #478) — see `isUnavailable` in getGasTankTokenDetails.
    if (totalBalanceGasTankDetails.isUnavailable) return 'error'
    if (!hasGasTank && isViewOnly && totalBalanceGasTankDetails.balanceFormatted) return 'balance'
    if (hasGasTank && totalBalanceGasTankDetails.balanceFormatted) return 'balance'
    if (hasGasTank && !totalBalanceGasTankDetails.balanceFormatted) return 'topup'

    return 'soon'
  }, [
    hasGasTank,
    isViewOnly,
    totalBalanceGasTankDetails.balanceFormatted,
    totalBalanceGasTankDetails.isUnavailable
  ])

  // Purposely don't disable the button (but block the onPress action) in
  // case of a tooltip, because it should be clickable to show the tooltip.
  const doesHaveTooltip = buttonState === 'soon' || buttonState === 'error'
  const disabled = !hasGasTank && !doesHaveTooltip
  const handleOnPress = useCallback(() => {
    if (doesHaveTooltip) return

    return onPress()
  }, [doesHaveTooltip, onPress])

  if (!portfolio.isReadyToVisualize) {
    return <SkeletonLoader lowOpacity width={160} height={28} borderRadius={8} />
  }

  return (
    <View>
      <AnimatedPressable
        ref={buttonRef}
        onPress={handleOnPress}
        disabled={disabled}
        style={
          {
            ...flexbox.directionRow,
            ...flexbox.center,
            ...spacings.phTy,
            ...spacings.pvMi,
            ...common.borderRadiusPrimary,
            ...removeTankBtnStyle,
            ...(!totalBalanceGasTankDetails.balanceFormatted && {
              borderWidth: 1,
              borderColor: theme.primaryLight
            }),
            ...(doesHaveTooltip && { cursor: 'default' })
          } as any
        }
        {...bindGasTankBtnAim}
        testID="dashboard-gas-tank-button"
      >
        <GasTankIcon
          width={20}
          height={20}
          color={
            themeType === THEME_TYPES.DARK
              ? theme.primaryBackgroundInverted
              : theme.primaryBackground
          }
        />
        {buttonState === 'balance' && (
          <Text
            style={[spacings.mlMi, { lineHeight: GAS_TANK_BUTTON_LINE_HEIGHT }]}
            fontSize={12}
            color={textColor}
          >
            <Trans
              values={{
                balance: balanceHidden
                  ? HIDDEN_BALANCE_MASK
                  : totalBalanceGasTankDetails.balanceUSDFormatted
              }}
            >
              <Text
                testID="dashboard-gas-tank-balance"
                weight="number_medium"
                fontSize={12}
                color={textColor}
              >
                {'{{balance}}'}
              </Text>{' '}
              <Text style={{ opacity: 0.57 }} fontSize={12} color={textColor}>
                on Gas Tank
              </Text>
            </Trans>
          </Text>
        )}

        {buttonState === 'topup' && (
          <Text
            style={[spacings.mhTy]}
            color={
              themeType === THEME_TYPES.DARK
                ? theme.primaryBackgroundInverted
                : theme.primaryBackground
            }
            weight="number_bold"
            fontSize={12}
          >
            {t('Top up Gas Tank')}
          </Text>
        )}
        {buttonState === 'soon' && (
          <View
            dataSet={createGlobalTooltipDataSet({
              id: 'gas-tank-soon',
              content: t('Not available for hardware wallets yet.'),
              hidden: buttonState !== 'soon'
            })}
          >
            <Text
              style={[spacings.mhTy]}
              color={
                themeType === THEME_TYPES.DARK
                  ? theme.primaryBackgroundInverted
                  : theme.primaryBackground
              }
              weight="number_bold"
              fontSize={12}
            >
              {isViewOnly ? t('') : t('Gas Tank Soon')}
            </Text>
          </View>
        )}
        {buttonState === 'error' && (
          <View
            dataSet={createGlobalTooltipDataSet({
              id: 'gas-tank-error',
              content: t('Unable to load Gas Tank data.')
            })}
          >
            <Text
              style={[spacings.mhTy]}
              color={
                themeType === THEME_TYPES.DARK
                  ? theme.primaryBackgroundInverted
                  : theme.primaryBackground
              }
              weight="number_bold"
              fontSize={12}
            >
              {t('Gas Tank Unavailable')}
            </Text>
          </View>
        )}
      </AnimatedPressable>
    </View>
  )
}

export default React.memo(GasTankButton)
